import { WheelData } from '../Wheel/types';
import { clamp, getQuantity } from '../../utils';
import {
  WheelCanvasStyle,
  WheelFace,
  WheelImage,
  WheelImageShell,
  WheelInnerCircle,
  WheelSeparator,
  WheelText,
} from './styles';

interface WheelCanvasProps extends DrawWheelProps {
  width: string;
  height: string;
  data: WheelData[];
}

interface DrawWheelProps {
  outerBorderColor: string;
  outerBorderWidth: number;
  innerRadius: number;
  innerBorderColor: string;
  innerBorderWidth: number;
  radiusLineColor: string;
  radiusLineWidth: number;
  fontFamily: string;
  fontWeight: number | string;
  fontSize: number;
  fontStyle: string;
  perpendicularText: boolean;
  prizeMap: number[][];
  textDistance: number;
}

interface WheelSegment {
  item: WheelData;
  startAngle: number;
  centerAngle: number;
  endAngle: number;
}

const BASE_WHEEL_SIZE = 900;
const BASE_OUTSIDE_RADIUS = BASE_WHEEL_SIZE / 2 - 10;
const OUTSIDE_RADIUS_PERCENT = (BASE_OUTSIDE_RADIUS / BASE_WHEEL_SIZE) * 100;
const BASE_IMAGE_SIZE = 200;
const BASE_IMAGE_SIZE_PERCENT = (BASE_IMAGE_SIZE / BASE_WHEEL_SIZE) * 100;

const getSegments = (data: WheelData[], quantity: number): WheelSegment[] => {
  let startAngle = 0;

  return data.map((item) => {
    const arc = ((item.optionSize || 1) * 360) / quantity;
    const segment = {
      item,
      startAngle,
      centerAngle: startAngle + arc / 2,
      endAngle: startAngle + arc,
    };

    startAngle += arc;

    return segment;
  });
};

const buildWheelBackground = (segments: WheelSegment[]): string => {
  if (!segments.length) {
    return 'transparent';
  }

  return `conic-gradient(from 90deg, ${segments
    .map(({ item, startAngle, endAngle }) => (
      `${item.style?.backgroundColor || 'transparent'} ${startAngle}deg ${endAngle}deg`
    ))
    .join(', ')})`;
};

const getPositionFromAngle = (angle: number, radiusPercent: number) => {
  const angleInRadians = (angle * Math.PI) / 180;

  return {
    left: 50 + Math.cos(angleInRadians) * radiusPercent,
    top: 50 + Math.sin(angleInRadians) * radiusPercent,
  };
};

const getImageOffsets = (image: WheelData['image']) => {
  const size = BASE_IMAGE_SIZE * (image?.sizeMultiplier || 1);
  const offsetX = (image?.offsetX || 0) / -2;
  const offsetY = image?.landscape
    ? (image?.offsetY || 0) / -2
    : (90 - (image?.offsetY || 0)) / 2;

  return {
    x: (offsetX / size) * 100,
    y: (offsetY / size) * 100,
  };
};

const WheelCanvas = ({
  data,
  outerBorderColor,
  outerBorderWidth,
  innerRadius,
  innerBorderColor,
  innerBorderWidth,
  radiusLineColor,
  radiusLineWidth,
  fontFamily,
  fontWeight,
  fontSize,
  fontStyle,
  perpendicularText,
  prizeMap,
  textDistance,
}: WheelCanvasProps): JSX.Element => {
  const quantity = getQuantity(prizeMap);
  const segments = getSegments(data, quantity);
  const background = buildWheelBackground(segments);
  const contentRadiusPercent = OUTSIDE_RADIUS_PERCENT * (clamp(0, 100, textDistance) / 100);
  const insideRadiusPercent = OUTSIDE_RADIUS_PERCENT * (clamp(0, 100, innerRadius) / 100);
  const separatorHeightPercent = OUTSIDE_RADIUS_PERCENT - insideRadiusPercent;
  const separatorBottomPercent = 50 + insideRadiusPercent;
  const outerBorderSize = Math.max(0, outerBorderWidth * 2);
  const innerBorderSize = Math.max(0, innerBorderWidth * 2);
  const separatorWidth = Math.max(0, radiusLineWidth * 2);

  return (
    <WheelCanvasStyle>
      <WheelFace
        $background={background}
        $outerBorderColor={outerBorderColor}
        $outerBorderWidth={outerBorderSize}
      />
      {separatorWidth > 0 && segments.map(({ startAngle }, index) => (
        <WheelSeparator
          key={`separator-${index}`}
          $color={radiusLineColor}
          $width={separatorWidth}
          $height={separatorHeightPercent}
          $bottom={separatorBottomPercent}
          $rotation={startAngle + 90}
        />
      ))}
      {insideRadiusPercent > 0 && (
        <WheelInnerCircle
          $diameter={insideRadiusPercent * 2}
          $borderColor={innerBorderColor}
          $borderWidth={innerBorderSize}
        />
      )}
      {segments.map(({ item, centerAngle }, index) => {
        const { left, top } = getPositionFromAngle(centerAngle, contentRadiusPercent);

        if (item.image?.uri) {
          const imageSizePercent = BASE_IMAGE_SIZE_PERCENT * (item.image.sizeMultiplier || 1);
          const imageWidthPercent = imageSizePercent * (item.image.landscape ? 1.8 : 1.35);
          const offsets = getImageOffsets(item.image);

          return (
            <WheelImageShell
              key={`wheel-image-${item.image.uri}-${index}`}
              $left={left}
              $top={top}
              $width={imageWidthPercent}
              $height={imageSizePercent}
              $rotation={centerAngle + (item.image.landscape ? 0 : 90)}
            >
              <WheelImage
                src={item.image.uri}
                alt={item.option || `premio-${index + 1}`}
                $offsetX={offsets.x}
                $offsetY={offsets.y}
              />
            </WheelImageShell>
          );
        }

        return (
          <WheelText
            key={`wheel-text-${item.option || index}`}
            $left={left}
            $top={top}
            $rotation={centerAngle + (perpendicularText ? 90 : 0)}
            $fontFamily={item.style?.fontFamily || fontFamily}
            $fontWeight={item.style?.fontWeight || fontWeight}
            $fontSize={item.style?.fontSize || fontSize}
            $fontStyle={item.style?.fontStyle || fontStyle}
            $textColor={item.style?.textColor}
          >
            {item.option || ''}
          </WheelText>
        );
      })}
    </WheelCanvasStyle>
  );
};

export default WheelCanvas;
